import { useLocation } from '@tanstack/react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import { Bot } from 'lucide-react'
import {
  screenataAssistantModeAtom,
  screenataAssistantOpenAtom,
} from '@/stores/screenataPageContextStore'
import { cn } from '@/lib/utils'
import { useScreenataPageChat } from '@/components/compliance/ScreenataPageLayout'
import { isWorkspaceChatPath } from '@/lib/screenata-phase'
import { useAskAi } from './useAskAi'

export function AskAiButton({
  variant = 'floating',
}: {
  variant?: 'floating' | 'header'
}) {
  const location = useLocation()
  const setAssistantMode = useSetAtom(screenataAssistantModeAtom)
  const isOpen = useAtomValue(screenataAssistantOpenAtom)
  const openAskAi = useAskAi()
  // When the surrounding ScreenataPageLayout has a `chatConfig`, the
  // button drives the layout's docked chat instead of the floating
  // workspace chat. This gives the page complete control over chat
  // state without round-tripping through global atoms.
  const pageChat = useScreenataPageChat()

  // Hide on the chat home itself and on individual thread URLs — the
  // workspace chat rail is already on screen there.
  if (isWorkspaceChatPath(location.pathname)) return null
  if (pageChat?.hasConfig && pageChat.isOpen) return null
  if (!pageChat?.hasConfig && isOpen) return null

  const handleClick = () => {
    if (pageChat?.hasConfig) {
      pageChat.open()
      return
    }
    setAssistantMode('sidebar')
    openAskAi({ sendMessage: false })
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      aria-label='Ask Vera'
      className={cn(
        'font-heading flex w-[94px] flex-nowrap items-center gap-2 text-[13px] font-semibold transition-all',
        variant === 'floating'
          ? 'fixed right-6 bottom-6 z-30 h-10 rounded-full border border-primary bg-primary px-4 text-primary-foreground shadow-[0_4px_14px_rgba(63,95,206,0.32)] hover:bg-primary/90 hover:shadow-[0_6px_18px_rgba(63,95,206,0.38)]'
          : 'h-8 w-auto rounded-sm border border-cobalt-interactive-border bg-cobalt-tonal px-3.5 whitespace-nowrap text-cobalt-strong shadow-xs hover:border-cobalt-interactive-border-hover hover:bg-cobalt-tonal-hover dark:border-cobalt-strong/30 dark:hover:border-cobalt-strong/40'
      )}
    >
      <Bot className='h-4 w-4 shrink-0' strokeWidth={2.1} />
      Ask Vera
    </button>
  )
}
